/**
 * Wishlist Management System
 * Uses localStorage with customer-specific keys
 */

(() => {
  'use strict';

  // Get customer ID from global variable or use 'guest'
  const customerId = window.Shopify?.customer?.id || 'guest';
  const WISHLIST_KEY = `arruda_wishlist_${customerId}`;

  /**
   * Get wishlist from localStorage
   * @returns {Array} Array of product handles
   */
  function getWishlist() {
    try {
      const wishlist = localStorage.getItem(WISHLIST_KEY);
      return wishlist ? JSON.parse(wishlist) : [];
    } catch (e) {
      console.error('Error reading wishlist:', e);
      return [];
    }
  }

  /**
   * Save wishlist to localStorage
   * @param {Array} wishlist - Array of product handles
   */
  function saveWishlist(wishlist) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
      dispatchWishlistEvent();
    } catch (e) {
      console.error('Error saving wishlist:', e);
    }
  }

  /**
   * Check if product is in wishlist
   * @param {string} handle - Product handle
   * @returns {boolean}
   */
  function isInWishlist(handle) {
    const wishlist = getWishlist();
    return wishlist.includes(handle);
  }

  /**
   * Add product to wishlist
   * @param {string} handle - Product handle
   */
  function addToWishlist(handle) {
    let wishlist = getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.unshift(handle); // Add to beginning
      saveWishlist(wishlist);
      return true;
    }
    return false;
  }

  /**
   * Remove product from wishlist
   * @param {string} handle - Product handle
   */
  function removeFromWishlist(handle) {
    let wishlist = getWishlist();
    const index = wishlist.indexOf(handle);
    if (index > -1) {
      wishlist.splice(index, 1);
      saveWishlist(wishlist);
      return true;
    }
    return false;
  }

  /**
   * Toggle product in wishlist
   * @param {string} handle - Product handle
   * @returns {boolean} true if added, false if removed
   */
  function toggleWishlist(handle) {
    if (isInWishlist(handle)) {
      removeFromWishlist(handle);
      return false;
    } else {
      addToWishlist(handle);
      return true;
    }
  }

  /**
   * Get wishlist count
   * @returns {number}
   */
  function getWishlistCount() {
    return getWishlist().length;
  }

  /**
   * Dispatch custom event when wishlist changes
   */
  function dispatchWishlistEvent() {
    window.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: {
        count: getWishlistCount(),
        items: getWishlist()
      }
    }));
  }

  /**
   * Update button state
   * @param {HTMLElement} button
   * @param {boolean} isActive
   */
  function updateButtonState(button, isActive) {
    button.classList.toggle('is-active', isActive);
    button.setAttribute('aria-pressed', isActive);

    const addText = button.querySelector('.wishlist-button__text--add');
    const removeText = button.querySelector('.wishlist-button__text--remove');

    if (addText) addText.style.display = isActive ? 'none' : 'inline';
    if (removeText) removeText.style.display = isActive ? 'inline' : 'none';
  }

  /**
   * Update wishlist counter in header
   */
  function updateWishlistCounter() {
    const counter = document.querySelector('[data-wishlist-count]');
    const count = getWishlistCount();

    if (counter) {
      counter.textContent = count;
      counter.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  /**
   * Initialize wishlist button
   * @param {HTMLElement} button
   */
  function initWishlistButton(button) {
    const handle = button.dataset.productHandle;
    if (!handle) return;

    // Set initial state
    const isActive = isInWishlist(handle);
    updateButtonState(button, isActive);

    // Add click handler
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const added = toggleWishlist(handle);
      updateButtonState(button, added);

      // Show feedback
      const message = added
        ? button.dataset.addedMessage || 'Adicionado aos favoritos'
        : button.dataset.removedMessage || 'Removido dos favoritos';

      showToast(message);
    });
  }

  /**
   * Show toast notification
   * @param {string} message
   */
  function showToast(message) {
    // Check if theme has a toast/notification system
    if (window.theme && window.theme.showToast) {
      window.theme.showToast(message);
      return;
    }

    // Fallback: simple toast
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: var(--button-border-radius, 0.25rem);
      z-index: 10000;
      animation: wishlistToastIn 0.3s ease;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = 'wishlistToastOut 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  /**
   * Initialize all wishlist buttons on page
   */
  function initAllButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach(initWishlistButton);
    updateWishlistCounter();
  }

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllButtons);
  } else {
    initAllButtons();
  }

  // Re-initialize on AJAX page updates (for Shopify sections)
  document.addEventListener('shopify:section:load', initAllButtons);

  // Update counter when wishlist changes
  window.addEventListener('wishlist:updated', updateWishlistCounter);

  // Expose global functions
  window.addToWishlist = addToWishlist;
  window.removeFromWishlist = removeFromWishlist;
  window.toggleWishlist = toggleWishlist;
  window.isInWishlist = isInWishlist;
  window.getWishlist = getWishlist;
  window.getWishlistCount = getWishlistCount;
  window.initWishlistButton = initWishlistButton;

})();

// Add CSS animations
const style = document.createElement('style');
style.textContent = `
  @keyframes wishlistToastIn {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(1rem);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @keyframes wishlistToastOut {
    from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(1rem);
    }
  }
`;
document.head.appendChild(style);
